import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";

function UInput({ label, mono, testId, ...props }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="group relative mt-3">
        <input data-testid={testId} {...props}
          className={`w-full border-b border-white/15 bg-transparent py-3 caret-mint placeholder:text-faint focus:outline-none ${mono ? "font-mono text-lg tracking-tight" : "text-lg"}`} />
        <span className="pointer-events-none absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-mint transition-transform duration-300 group-focus-within:scale-x-100" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pricing, setPricing] = useState({ price_per_1m: 5000, signup_bonus_tokens: 500000 });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/public-config").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  const price = `Rp${pricing.price_per_1m.toLocaleString("id-ID")}`;
  const bonus = pricing.signup_bonus_tokens >= 1_000_000
    ? `${pricing.signup_bonus_tokens / 1_000_000} juta`
    : `${pricing.signup_bonus_tokens / 1_000} ribu`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = isLogin
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      nav(data.account?.wa_status === "connected" ? "/chats" : "/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-glow flex min-h-screen items-center justify-center bg-ink px-6 py-16">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mx-auto mb-6 flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-md border border-mint/20 bg-mint/[0.05] px-5 py-3" role="img" aria-label="Bot sedang mengetik">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-2 w-2 animate-bounce-dot rounded-full bg-mint" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <h1 className="font-display text-center text-4xl font-semibold leading-[1.1] tracking-[-0.03em] md:text-5xl">
          {isLogin ? <>Masuk ke <span className="text-mint">ChatSensei</span></> : <>Buat akun <span className="text-mint">gratis</span></>}
        </h1>
        <p className="mt-4 text-center text-muted">
          {isLogin ? "Lanjutkan mengelola percakapan Anda." : `Gratis ${bonus} token untuk memulai.`}
        </p>
        <form onSubmit={submit} className="mt-10 space-y-7">
          {!isLogin && (
            <UInput label="Nama" testId="auth-name-input" required placeholder="Nama Anda"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          <UInput label="Email" testId="auth-email-input" type="email" required placeholder="anda@bisnis.id"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <UInput label="Password" testId="auth-password-input" type="password" required minLength={6} placeholder="••••••••"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy}
            className="flex w-full items-center justify-center gap-2.5 rounded-full bg-mint py-4 text-[15px] font-semibold text-ink transition hover:-translate-y-px hover:opacity-95 disabled:pointer-events-none disabled:opacity-40">
            {busy ? "Memproses…" : isLogin ? "Masuk" : "Daftar Gratis"}
            {!busy && <ArrowRight size={16} strokeWidth={2.4} />}
          </button>
        </form>
        <p className="mt-8 text-center text-sm text-muted">
          {isLogin ? "Belum punya akun? " : "Sudah punya akun? "}
          <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="font-semibold text-mint hover:underline">
            {isLogin ? "Daftar" : "Masuk"}
          </Link>
        </p>
        <p className="mt-6 text-center font-mono text-xs text-faint">{price} / 1 juta token · tanpa langganan</p>
      </div>
    </div>
  );
}
